// src/components/FileManager.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import styled from "styled-components";
import {
  listOrgArticles,
  getArticle,
  deleteArticle,
  updateArticle,
  renameArticle,
  getDisplayStatus,
} from "../lib/articles";
import { listProjectsForUser } from "../lib/projects";

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const Sheet = styled.div`
  width: 80%;
  max-height: 80vh;
  overflow: auto;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  padding: 18px 18px 10px;
`;
const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
`;
const Title = styled.h3`
  margin: 0;
`;
const Close = styled.button`
  border: none;
  background: #eee;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
`;

const ProjectBar = styled.div`
  margin-top: 8px;
  display: flex;
  gap: 8px;
  align-items: center;
`;
const Label = styled.span`
  font-size: 14px;
  color: #374151;
  font-weight: 600;
`;
const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 12px;
  font-size: 14px;
  table-layout: fixed;
  th,
  td {
    padding: 5px 10px;
    text-align: left;
  }
  th {
    font-weight: 600;
    color: #374151;
    background: white;
  }
  th:nth-child(1),
  td:nth-child(1) {
    width: 30%;
    max-width: 30%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  th:nth-child(5),
  td:nth-child(5) {
    width: 5%;
    max-width: max-content;
    white-space: nowrap;
    min-width: max-content;
    text-align: center;
    margin-right: 25px;
  }

  /* Status column (example: column 4) */
  th:nth-child(4),
  td:nth-child(4) {
    width: max-content;
    min-width: max-content;
    white-space: nowrap;
  }
`;
const RowActions = styled.div`
  display: flex;
  gap: 8px;
`;
const Btn = styled.button`
  border: 1px solid #ddd;
  background: #f9f9f9;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
`;
const Input = styled.input`
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 6px;
`;

const ProgressPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 10px;
  border-radius: 300px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: ${({ status }) => {
    switch (status) {
      case "Edits Needed":
        return "#e8394f";
      case "Ready for Review":
        return "#00876e";
      case "Ready for Approval":
        return "#00876e";
      case "Ready to Publish":
        return "#1764e8";
      case "In Progress":
      default:
        return "#a46126";
    }
  }};
  background: ${({ status }) => {
    switch (status) {
      case "Edits Needed":
        return "#fbe8ec";
      case "Ready for Review":
        return "#e0f0ee";
      case "Ready for Approval":
        return "#e0f0ee";
      case "Ready to Publish":
        return "#ccdeff";
      case "In Progress":
      default:
        return "#fef7e6";
    }
  }};
`;

const ActionsCell = styled.td`
  position: relative;
  text-align: right;
`;

const MenuButton = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 4px 8px;
  font-size: 20px;
  line-height: 1;
  color: #6b7280; /* gray-500 */
  border-radius: 999px;

  &:hover {
    background: #f3f4f6; /* gray-100 */
    color: #111827; /* gray-900 */
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: 4px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18);
  padding: 4px 0;
  min-width: 140px;
  z-index: 10;
`;

const DropdownItem = styled.button`
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 8px 12px;
  font-size: 14px;
  color: #111827;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }

  &.danger {
    color: #b91c1c; /* red-700 */
  }
`;

export default function FileManager({
  isOpen,
  onClose,
  onOpenArticle,
  beginGenerating,
  endGenerating,
  // NEW: pass these in from parent (ContentGenerator / OrganizationPortal)
  orgId,
  currentUserId,
  currentUserRole,
  organization,
  profile,
}) {
  if (!isOpen) return null;

  const isAdmin =
    profile?.role === "Admin" ||
    profile?.role === "superAdmin" ||
    profile?.isTrueSuperAdmin === true;

  const [rows, setRows] = useState([]);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);

  // NEW: project list and selection
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("all");
  const [sortConfig, setSortConfig] = useState({
    key: "updated_at",
    direction: "desc", // newest first by default
  });

  const getProjectLabel = useCallback(
    (row) => {
      if (!row.project_id) return "Unassigned";
      return projects.find((p) => p.id === row.project_id)?.title || "Unknown";
    },
    [projects]
  );

  const handleOpenArticle = async (article) => {
    try {
      const full = article?.content_html
        ? article
        : await getArticle(article.id);

      if (typeof onOpenArticle === "function") {
        onOpenArticle(full);
      }

      if (typeof onClose === "function") {
        onClose();
      }
    } catch (err) {
      console.error("Error opening article:", err);
      alert("Failed to open article.");
    } finally {
      setOpenMenuId(null);
    }
  };

  const handleRenameCommit = async (rowId) => {
    if (!isAdmin) return;

    try {
      await renameArticle(rowId, renameValue);
      setRows((prev) =>
        prev.map((x) => (x.id === rowId ? { ...x, title: renameValue } : x))
      );
    } catch (e) {
      console.error("Error renaming article:", e);
      alert("Failed to rename article.");
    } finally {
      setRenamingId(null);
      setRenameValue("");
    }
  };

  const handleDelete = async (rowId) => {
    if (!isAdmin) return;

    try {
      await deleteArticle(rowId);
      setRows((prev) => prev.filter((x) => x.id !== rowId));
    } catch (e) {
      console.error("Error deleting article:", e);
      alert("Failed to delete article.");
    }
  };

  // Fetch projects user can see when opened
  useEffect(() => {
    if (!isOpen || !orgId || !currentUserId) return;
    (async () => {
      try {
        beginGenerating?.("Loading your projects…");
        const list = await listProjectsForUser({
          user_id: currentUserId,
          role: currentUserRole,
          company_id: orgId,
        });
        // inside the effect that loads projects
        const augmented = [
          { id: "__UNASSIGNED__", title: "Unassigned Files" },
          ...list,
        ];
        setProjects(augmented);

        // don’t override a user choice; only set default the first time
        setSelectedProjectId((prev) => (prev ? prev : "all"));
      } catch (e) {
        console.error(e);
      } finally {
        endGenerating?.();
      }
    })();
  }, [isOpen, orgId, currentUserId, currentUserRole]); // eslint-disable-line

  // Fetch articles for selected project
  useEffect(() => {
    if (!isOpen || !orgId) return;
    (async () => {
      try {
        beginGenerating?.("Loading files…");

        const params = { org_id: orgId };
        if (selectedProjectId === "__UNASSIGNED__") {
          // only unassigned files
          params.project_id = null;
        } else if (selectedProjectId !== "all") {
          // a specific project id
          params.project_id = selectedProjectId;
        }
        // if selectedProjectId === "all", we leave params.project_id undefined
        // → listOrgArticles sees "no project filter"

        const list = await listOrgArticles(params);
        setRows(list);
      } catch (e) {
        console.error(e);
      } finally {
        endGenerating?.();
      }
    })();
  }, [isOpen, selectedProjectId]);

  const sortedRows = useMemo(() => {
    const data = [...rows];

    data.sort((a, b) => {
      let aVal;
      let bVal;

      switch (sortConfig.key) {
        case "title":
          aVal = a.title || "";
          bVal = b.title || "";
          break;

        case "project":
          aVal = getProjectLabel(a);
          bVal = getProjectLabel(b);
          break;

        case "creator":
          aVal = a.creator_name || a.created_by_email || a.created_by || "";
          bVal = b.creator_name || b.created_by_email || b.created_by || "";
          break;

        case "status":
          aVal = a.progress || "In Progress";
          bVal = b.progress || "In Progress";
          break;

        case "score": {
          const aScore = getScoreValue(a);
          const bScore = getScoreValue(b);
          return sortConfig.direction === "asc"
            ? aScore - bScore
            : bScore - aScore;
        }

        case "updated_at":
          aVal = a.updated_at || "";
          bVal = b.updated_at || "";
          // numeric compare for dates
          const aTime = aVal ? new Date(aVal).getTime() : 0;
          const bTime = bVal ? new Date(bVal).getTime() : 0;
          return sortConfig.direction === "asc" ? aTime - bTime : bTime - aTime;

        default:
          return 0;
      }

      // string compare
      const cmp = aVal.localeCompare(bVal, undefined, {
        sensitivity: "base",
      });

      return sortConfig.direction === "asc" ? cmp : -cmp;
    });

    return data;
  }, [rows, sortConfig, getProjectLabel]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // toggle direction
        return {
          key,
          direction: prev.direction === "asc" ? "desc" : "asc",
        };
      }
      // new column → default to ascending
      return { key, direction: "asc" };
    });
  };

  const renderSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  return (
    <Backdrop>
      <Sheet>
        <Head>
          <Title>My Articles</Title>
          <Close onClick={onClose}>Close</Close>
        </Head>

        {/* NEW: Project filter under title */}
        <ProjectBar>
          <Label>Project</Label>
          <Select
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
          >
            <option value="all">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.title}
              </option>
            ))}
          </Select>
        </ProjectBar>

        <Table>
          <thead>
            <tr>
              <th
                onClick={() => handleSort("title")}
                style={{ cursor: "pointer" }}
              >
                Title{renderSortIndicator("title")}
              </th>
              <th
                onClick={() => handleSort("project")}
                style={{ cursor: "pointer" }}
              >
                Project{renderSortIndicator("project")}
              </th>
              <th
                onClick={() => handleSort("creator")}
                style={{ cursor: "pointer" }}
              >
                Writer{renderSortIndicator("creator")}
              </th>
              <th
                onClick={() => handleSort("status")}
                style={{ cursor: "pointer" }}
              >
                Status{renderSortIndicator("status")}
              </th>
              <th
                onClick={() => handleSort("score")}
                style={{ cursor: "pointer" }}
              >
                Score{renderSortIndicator("score")}
              </th>
              <th
                onClick={() => handleSort("updated_at")}
                style={{ cursor: "pointer" }}
              >
                Last Updated{renderSortIndicator("updated_at")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {sortedRows.map((r) => {
              const projectLabel = getProjectLabel(r);

              return (
                <tr key={r.id}>
                  {/* Title */}
                  <td>
                    {renamingId === r.id ? (
                      <Input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleRenameCommit(r.id);
                          } else if (e.key === "Escape") {
                            setRenamingId(null);
                            setRenameValue("");
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleOpenArticle(r)}
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          margin: 0,
                          cursor: "pointer",
                          textAlign: "left",
                          font: "inherit",
                          color: "inherit",
                          fontWeight: 800,
                          width: "100%",
                        }}
                        title="Open article"
                      >
                        {r.title || "Untitled"}
                      </button>
                    )}
                  </td>

                  {/* Project */}
                  <td>{projectLabel}</td>

                  {/* Creator */}
                  <td>
                    {r.creator_name || r.created_by_email || r.created_by}
                  </td>

                  {/* Status */}
                  <td>
                    <ProgressPill
                      status={getDisplayStatus(r.progress, organization?.ai_enabled)}
                    >
                      {getDisplayStatus(r.progress, organization?.ai_enabled)}
                    </ProgressPill>
                  </td>

                  <td style={{ textAlign: "center" }}>{r.score ?? "-"}</td>

                  {/* Last Updated */}
                  <td>{new Date(r.updated_at).toLocaleString()}</td>

                  {/* Actions */}

                  <ActionsCell>
                    <MenuButton
                      type="button"
                      onClick={() =>
                        setOpenMenuId((current) =>
                          current === r.id ? null : r.id
                        )
                      }
                    >
                      ⋯
                    </MenuButton>

                    {openMenuId === r.id && (
                      <DropdownMenu>
                        <DropdownItem
                          type="button"
                          onClick={async () => {
                            try {
                              beginGenerating?.("Opening article…");
                              const article = await getArticle(r.id); // <- fetch full article
                              onOpenArticle?.(article); // <- send full article to TextBox
                              onClose?.(); // <- close the FileManager
                            } catch (err) {
                              console.error("Error opening article:", err);
                            } finally {
                              setOpenMenuId(null);
                              endGenerating?.();
                            }
                          }}
                        >
                          Open
                        </DropdownItem>

                        {isAdmin && (
                          <DropdownItem
                            type="button"
                            onClick={() => {
                              setRenamingId(r.id);
                              setRenameValue(r.title || "");
                              setOpenMenuId(null);
                            }}
                          >
                            Rename
                          </DropdownItem>
                        )}

                        {isAdmin && (
                          <DropdownItem
                            type="button"
                            className="danger"
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Are you sure you want to delete this article?"
                                )
                              ) {
                                handleDelete(r.id);
                              }
                              setOpenMenuId(null);
                            }}
                          >
                            Delete
                          </DropdownItem>
                        )}
                      </DropdownMenu>
                    )}
                  </ActionsCell>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Sheet>
    </Backdrop>
  );
}
